import { useCallback, useEffect, useState } from "react";
import type { Mengen, MengenWall } from "@bidi-handel/core/schemas";
import { api, ApiError } from "../../api/client";
import { useApi } from "../../hooks/useApi";
import { Icon } from "../../shell/Icon";
import { PlanCanvasEditor, type OcgLayer } from "./mengen/PlanCanvasEditor";
import { PromptModal } from "./mengen/PromptModal";
import { RaumDetailModal } from "./mengen/RaumDetailModal";
import { isSuspiciousShape } from "./mengen/plan-geometry";

type MengenRoom = Mengen["plaene"][number]["rooms"][number];
type EditorMode = "view" | "scale" | "draw" | "wall";
type Point = { x: number; y: number };
type PromptState =
  | { kind: "distance"; p1: Point; p2: Point }
  | { kind: "raumhoehe" };

/**
 * Mengen-Tab (Pfad B, Slice 3) — Editor für die deterministische Vektor-Engine.
 *
 * Lädt 02-mengen.json (Räume + GeoJSON-Polygone + Maßstab) und das Plan-PDF.
 * Links: PlanCanvasEditor (pdfjs-Render + Polygon-Overlay, Vertex-Drag,
 * 2-Punkt-Maßstab). Rechts: Raum-Liste + Maßstab/Raumhöhe-Steuerung. Jede
 * Änderung persistiert via PATCH (Server recomputed Fläche/Umfang/Wand + vor-LV);
 * nachgelagerte Phasen werden NICHT auto-neu-erzeugt — Hinweis zum Re-Run.
 */
export function MengenTab({
  tenantId,
  anfrageId,
}: {
  tenantId: string;
  anfrageId: string;
}) {
  const res = useApi<Mengen | null>(
    () =>
      api.getMengen(tenantId, anfrageId).catch((err) => {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }),
    [tenantId, anfrageId]
  );

  const [mengen, setMengen] = useState<Mengen | null>(null);
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [mode, setMode] = useState<EditorMode>("view");
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [prompt, setPrompt] = useState<PromptState | null>(null);
  const [detailRoomId, setDetailRoomId] = useState<string | null>(null);
  // Wohneinheiten-Modus (#5A): Multi-Select + Benennung.
  const [weMode, setWeMode] = useState(false);
  const [weSelection, setWeSelection] = useState<Set<string>>(new Set());
  const [weName, setWeName] = useState("");
  // #10: fokussierte Wohneinheit (Chip-Klick) — hebt ihre Räume in Liste + Plan
  // hervor und sortiert sie nach oben.
  const [focusedWe, setFocusedWe] = useState<string | null>(null);
  // #11: OCG-Layer der Plan-PDF + gewünschte Sichtbarkeit + Dropdown-Status.
  const [ocgLayers, setOcgLayers] = useState<OcgLayer[]>([]);
  const [ocgVisibility, setOcgVisibility] = useState<Record<string, boolean>>({});
  const [layerMenuOpen, setLayerMenuOpen] = useState(false);
  const handleOcgLayers = useCallback((layers: OcgLayer[]) => {
    setOcgLayers(layers);
    setOcgVisibility(Object.fromEntries(layers.map((l) => [l.id, l.visible])));
  }, []);

  useEffect(() => {
    if (res.state.status === "ok") setMengen(res.state.data);
  }, [res.state]);

  if (res.state.status === "loading") {
    return (
      <div className="ueber">
        <div className="dim" style={{ padding: 40 }}>
          <span className="spinner" style={{ borderColor: "var(--acc)", borderRightColor: "transparent" }} />{" "}
          Mengen werden geladen …
        </div>
      </div>
    );
  }
  if (res.state.status === "error") {
    return (
      <div className="ueber">
        <div className="warn-line" style={{ borderLeftColor: "var(--bad)" }}>
          <strong>Mengen nicht ladbar:</strong>{" "}
          <span className="mono">{res.state.error.message}</span>
        </div>
      </div>
    );
  }
  if (!mengen) {
    return <NotGeneratedNotice />;
  }

  const planIndex = 0;
  const plan = mengen.plaene[planIndex];
  if (!plan) return <NotGeneratedNotice />;

  const pdfUrl = api.eingangFileUrl(tenantId, anfrageId, plan.sourceFile);

  async function withBusy(fn: () => Promise<Mengen>, markDirty = true) {
    setBusy(true);
    setActionError(null);
    try {
      const updated = await fn();
      setMengen(updated);
      // WE-Zuordnung (#5A) ist organisatorisch → kein „Pipeline neu"-Hinweis.
      if (markDirty) setDirty(true);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const commitPolygon = (polygonId: string, coordinates: number[][][]) =>
    withBusy(() =>
      api.patchMengenPolygon(tenantId, anfrageId, polygonId, { planIndex, coordinates })
    );

  // 2-Punkt-Maßstab: Editor liefert die zwei Punkte, die reale Distanz fragt
  // ein In-App-Modal ab (window.prompt crasht im eingebetteten Browser).
  const scalePicked = (p1: Point, p2: Point) => {
    setMode("view");
    setPrompt({ kind: "distance", p1, p2 });
  };

  const confirmPrompt = (value: number) => {
    const p = prompt;
    setPrompt(null);
    if (!p) return;
    if (p.kind === "distance") {
      void withBusy(() =>
        api.patchMengenScale(tenantId, anfrageId, {
          planIndex,
          p1: p.p1,
          p2: p.p2,
          realMeters: value,
        })
      );
    } else {
      void withBusy(async () => {
        const r = await api.recomputeMengen(tenantId, anfrageId, {
          raumhoeheM: value,
        });
        return r.mengen;
      });
    }
  };

  // Klick auf denselben Modus schaltet zurück auf „view".
  const toggleMode = (m: EditorMode) => setMode((cur) => (cur === m ? "view" : m));

  // P2b: neu gezeichnetes Raum-Polygon anlegen.
  const createPolygon = (coordinates: number[][][]) => {
    setMode("view");
    void withBusy(async () => {
      const r = await api.addMengenPolygon(tenantId, anfrageId, { planIndex, coordinates });
      setSelectedRoomId(r.roomId);
      return r.mengen;
    });
  };

  // P2b: gewählten Raum löschen.
  const deleteRoom = (roomId: string) => {
    void withBusy(async () => {
      const m = await api.deleteMengenPolygon(tenantId, anfrageId, roomId, { planIndex });
      setSelectedRoomId((id) => (id === roomId ? null : id));
      return m;
    });
  };

  // P2c: Wand-Striche des gewählten Raums speichern.
  const commitWalls = (walls: MengenWall[]) => {
    if (!selectedRoomId) return;
    void withBusy(() =>
      api.patchMengenRoom(tenantId, anfrageId, selectedRoomId, { planIndex, walls })
    );
  };

  const rooms = plan.rooms;
  const sumBoden = rooms.reduce((s, r) => s + r.bodenflaecheM2, 0);
  const selectedRoom = rooms.find((r) => r.roomId === selectedRoomId) ?? null;
  const detailRoom = rooms.find((r) => r.roomId === detailRoomId) ?? null;

  // Distinkte Wohneinheiten + Raumzahl (für Legende/Anzeige).
  const weCounts = new Map<string, number>();
  for (const r of rooms) {
    if (r.wohneinheit) weCounts.set(r.wohneinheit, (weCounts.get(r.wohneinheit) ?? 0) + 1);
  }
  const weList = [...weCounts.keys()].sort((a, b) =>
    a.localeCompare(b, "de", { numeric: true })
  );

  // #10: Räume der fokussierten WE + ggf. der Einzel-/WE-Mode-Auswahl bilden die
  // „nach oben"-Gruppe; im Plan werden die WE-Räume hervorgehoben.
  const focusIds = new Set<string>();
  if (focusedWe) {
    for (const r of rooms) if (r.wohneinheit === focusedWe) focusIds.add(r.roomId);
  }
  if (selectedRoomId) focusIds.add(selectedRoomId);
  if (weMode) for (const id of weSelection) focusIds.add(id);
  // Selektierte/fokussierte Räume prägnant nach oben, Rest in Originalreihenfolge.
  const orderedRooms = [
    ...rooms.filter((r) => focusIds.has(r.roomId)),
    ...rooms.filter((r) => !focusIds.has(r.roomId)),
  ];
  // Plan-Highlight: die WE-Gruppe (bzw. WE-Mode-Auswahl) — NICHT der Einzel-Pick,
  // der schon als selectedRoomId hervorgehoben wird.
  const highlightRoomIds = new Set<string>();
  if (focusedWe) {
    for (const r of rooms) if (r.wohneinheit === focusedWe) highlightRoomIds.add(r.roomId);
  }
  if (weMode) for (const id of weSelection) highlightRoomIds.add(id);

  const handleWeChip = (we: string) => {
    setFocusedWe((cur) => (cur === we ? null : we));
    if (weMode) {
      // Im WE-Modus zugleich alle Räume dieser WE markieren (Re-Zuweisung).
      const ids = rooms.filter((r) => r.wohneinheit === we).map((r) => r.roomId);
      setWeSelection(new Set(ids));
    }
  };

  const toggleWeMode = () => {
    setWeMode((on) => !on);
    setWeSelection(new Set());
    setWeName("");
    setSelectedRoomId(null);
    setFocusedWe(null);
    setMode("view");
  };
  const toggleWeSelection = (roomId: string) =>
    setWeSelection((prev) => {
      const next = new Set(prev);
      if (next.has(roomId)) next.delete(roomId);
      else next.add(roomId);
      return next;
    });
  const assignWe = (wohneinheit: string | null) => {
    if (weSelection.size === 0) return;
    const roomIds = [...weSelection];
    void withBusy(async () => {
      const r = await api.patchMengenWohneinheit(tenantId, anfrageId, {
        planIndex,
        roomIds,
        wohneinheit,
      });
      setWeSelection(new Set());
      setWeName("");
      return r.mengen;
    }, false);
  };
  const autoWe = () =>
    withBusy(async () => {
      const r = await api.autoMengenWohneinheit(tenantId, anfrageId, planIndex);
      if (r.assigned === 0) {
        setActionError(
          "Keine WE-Stempel im Plan gefunden (oder Scan-PDF) — bitte manuell zuweisen."
        );
      }
      return r.mengen;
    }, false);

  return (
    <div className="ueber" style={{ display: "grid", gap: 14 }}>
      {/* Kopf: Maßstab + Modus + Status */}
      <section className="block">
        <div className="block-head">
          <span className="uppercase-mini" style={{ color: "var(--acc)" }}>
            // MENGEN · PFAD B · VEKTOR-ENGINE
          </span>
          <span className="block-sep" />
          <span className="uppercase-mini" style={{ color: "var(--fg-faint)" }}>
            {plan.sourceFile} · {rooms.length} Räume · Σ {sumBoden.toFixed(1)} m²
          </span>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", padding: "4px 2px" }}>
          <span className="pill acc mono">
            <span className="dot" /> 1:{plan.scale.ratio} ({plan.scale.source})
          </span>
          <span className="pill mono">Raumhöhe {mengen.raumhoeheM} m</span>
          <button
            type="button"
            className={`btn ${mode === "scale" ? "primary" : ""}`}
            disabled={busy}
            onClick={() => toggleMode("scale")}
          >
            <Icon name="spark" size={13} />{" "}
            {mode === "scale" ? "2 Punkte klicken …" : "Maßstab setzen"}
          </button>
          <button
            type="button"
            className={`btn ${mode === "draw" ? "primary" : ""}`}
            disabled={busy}
            onClick={() => toggleMode("draw")}
            title="Neuen Raum zeichnen: klicken für Vertices, ersten Punkt erneut anklicken schließt. Esc verwirft."
          >
            {mode === "draw" ? "Raum: Punkte klicken …" : "Raum zeichnen"}
          </button>
          <button
            type="button"
            className={`btn ${mode === "wall" ? "primary" : ""}`}
            disabled={busy || !selectedRoomId}
            onClick={() => toggleMode("wall")}
            title="Wände des gewählten Raums zeichnen: zwei Punkte = ein Strich. Endpunkt ziehen = verlängern/verkürzen, Alt-Klick = löschen."
          >
            {mode === "wall" ? "Wand: 2 Punkte klicken …" : "Wände zeichnen"}
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy}
            onClick={() => setPrompt({ kind: "raumhoehe" })}
          >
            Raumhöhe ändern
          </button>
          <button
            type="button"
            className={`btn ${weMode ? "primary" : ""}`}
            disabled={busy}
            onClick={toggleWeMode}
            title="Räume markieren und einer Wohneinheit (WE/WHG) zuordnen"
          >
            {weMode ? "Wohneinheiten: fertig" : "Wohneinheiten"}
          </button>
          {ocgLayers.length > 0 && (
            <div style={{ position: "relative" }}>
              <button
                type="button"
                className={`btn ${layerMenuOpen ? "primary" : ""}`}
                onClick={() => setLayerMenuOpen((o) => !o)}
                title="PDF-Layer (OCG) ein-/ausblenden — erleichtert Bemessen/Nachmessen"
              >
                <Icon name="layers" size={13} /> Layer ({ocgLayers.length})
              </button>
              {layerMenuOpen && (
                <>
                  {/* Klick außerhalb schließt das Menü. */}
                  <div
                    onClick={() => setLayerMenuOpen(false)}
                    style={{ position: "fixed", inset: 0, zIndex: 40 }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: "calc(100% + 4px)",
                      left: 0,
                      zIndex: 50,
                      minWidth: 240,
                      maxHeight: 320,
                      overflow: "auto",
                      padding: 8,
                      background: "var(--bg-elevated)",
                      border: "1px solid var(--line)",
                      borderRadius: 8,
                      boxShadow: "0 20px 50px -16px oklch(0 0 0 / 0.7)",
                    }}
                  >
                    <div
                      className="uppercase-mini"
                      style={{ color: "var(--fg-dim)", padding: "2px 6px 6px" }}
                    >
                      PDF-Layer
                    </div>
                    {ocgLayers.map((l) => (
                      <label
                        key={l.id}
                        style={{
                          display: "flex",
                          gap: 8,
                          alignItems: "center",
                          padding: "5px 6px",
                          cursor: "pointer",
                          fontSize: 13,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={ocgVisibility[l.id] ?? l.visible}
                          onChange={(e) =>
                            setOcgVisibility((v) => ({
                              ...v,
                              [l.id]: e.currentTarget.checked,
                            }))
                          }
                        />
                        <span style={{ color: "var(--fg)" }}>{l.name}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          {selectedRoom && (
            <>
              <button
                type="button"
                className="btn"
                disabled={busy}
                onClick={() => setDetailRoomId(selectedRoom.roomId)}
              >
                Bemustern / Details
              </button>
              <button
                type="button"
                className="btn ghost"
                disabled={busy}
                onClick={() => deleteRoom(selectedRoom.roomId)}
                title={`Raum „${selectedRoom.label}" löschen`}
              >
                Raum löschen
              </button>
            </>
          )}
          {busy && (
            <span className="dim mono" style={{ fontSize: 11 }}>
              speichert …
            </span>
          )}
        </div>
        {plan.scale.source === "default" && (
          <div className="warn-line" style={{ marginTop: 8 }}>
            Kein Maßstab erkannt — Default 1:100 angesetzt. Bitte über „Maßstab
            setzen" zwei Punkte mit bekannter realer Distanz kalibrieren.
          </div>
        )}
        {actionError && (
          <div className="warn-line" style={{ marginTop: 8, borderLeftColor: "var(--bad)" }}>
            <span className="mono">{actionError}</span>
          </div>
        )}
        {dirty && (
          <div className="warn-line" style={{ marginTop: 8 }}>
            Mengen geändert. Damit Match / Preise / Angebot die neuen Werte
            nutzen, die <strong>Pipeline ab Phase 3 neu laufen lassen</strong>
            {" "}(„Pipeline starten").
          </div>
        )}
      </section>

      {weMode && (
        <section className="block">
          <div className="block-head">
            <span className="uppercase-mini" style={{ color: "var(--acc)" }}>
              // WOHNEINHEITEN · {weSelection.size} Raum/Räume markiert
            </span>
          </div>
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              alignItems: "center",
              padding: "4px 2px",
            }}
          >
            <input
              type="text"
              className="input"
              placeholder="z.B. WE 1 / WHG 3"
              value={weName}
              onChange={(e) => {
                const v = e.currentTarget.value;
                setWeName(v);
              }}
              style={{ width: 160 }}
              aria-label="Wohneinheit-Bezeichnung"
            />
            <button
              type="button"
              className="btn primary"
              disabled={busy || weSelection.size === 0 || !weName.trim()}
              onClick={() => assignWe(weName)}
              title="Markierte Räume dieser Wohneinheit zuweisen"
            >
              Zuweisen
            </button>
            <button
              type="button"
              className="btn ghost"
              disabled={busy || weSelection.size === 0}
              onClick={() => assignWe(null)}
              title="WE-Zuordnung der markierten Räume entfernen"
            >
              Zuordnung entfernen
            </button>
            <span className="block-sep" />
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={autoWe}
              title="WE-Stempel im Plan erkennen und Räume automatisch zuordnen"
            >
              <Icon name="spark" size={13} /> Aus Plan-Stempeln vorbelegen
            </button>
          </div>
          {weList.length > 0 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
              {weList.map((we) => (
                <button
                  key={we}
                  type="button"
                  className={`pill mono ${focusedWe === we ? "acc" : ""}`}
                  style={{ cursor: "pointer", fontSize: 11 }}
                  onClick={() => handleWeChip(we)}
                  title={`Alle Räume der ${we} markieren`}
                >
                  {we} · {weCounts.get(we)} R
                </button>
              ))}
            </div>
          )}
          <div className="dim" style={{ fontSize: 12, marginTop: 8 }}>
            Räume in der Liste rechts (oder im Plan) anklicken zum Markieren — oder
            oben eine WE anklicken, um alle ihre Räume zu markieren. Die Zuordnung
            ist rein organisatorisch (kein Pipeline-Re-Run nötig).
          </div>
        </section>
      )}

      {plan.warnings.length > 0 && (
        <section className="block">
          <div className="block-head">
            <span className="uppercase-mini" style={{ color: "var(--warn, orange)" }}>
              // HINWEISE
            </span>
          </div>
          <div style={{ display: "grid", gap: 6 }}>
            {plan.warnings.map((w, i) => (
              <div key={i} className="warn-line">
                {w}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Editor + Raum-Liste */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 320px", gap: 14, alignItems: "start" }}>
        <section className="block" style={{ minWidth: 0 }}>
          <div className="block-head">
            <span className="uppercase-mini">// PLAN</span>
            <span className="block-sep" />
            <span className="uppercase-mini" style={{ color: "var(--fg-faint)" }}>
              {mode === "scale"
                ? "Maßstab: 2 Punkte klicken"
                : mode === "draw"
                  ? "Raum: Vertices klicken · ersten Punkt erneut = schließen · Esc = verwerfen"
                  : mode === "wall"
                    ? "Wand: 2 Punkte = Strich · Endpunkt ziehen · Alt-Klick = löschen"
                    : "Raum klicken → Vertices ziehen · Mittelpunkt-Plus = Vertex einfügen · Alt-Klick = löschen"}
            </span>
          </div>
          <PlanCanvasEditor
            pdfUrl={pdfUrl}
            pageIndex={plan.pageIndex}
            ratio={plan.scale.ratio}
            features={plan.polygons.features}
            selectedRoomId={selectedRoomId}
            highlightRoomIds={highlightRoomIds}
            onOcgLayers={handleOcgLayers}
            ocgVisibility={ocgVisibility}
            selectedRoomWalls={selectedRoom?.walls ?? []}
            mode={mode}
            onSelectRoom={setSelectedRoomId}
            onCommitPolygon={commitPolygon}
            onScalePicked={scalePicked}
            onCreatePolygon={createPolygon}
            onCommitWalls={commitWalls}
          />
        </section>

        <section className="block" style={{ minWidth: 0 }}>
          <div className="block-head">
            <span className="uppercase-mini">
              // RÄUME ({rooms.length}){weMode ? " · MARKIEREN" : ""}
            </span>
          </div>
          {weList.length > 0 && (
            <div
              style={{ display: "flex", gap: 6, flexWrap: "wrap", padding: "0 2px 8px" }}
            >
              {weList.map((we) => (
                <button
                  key={we}
                  type="button"
                  className={`pill mono ${focusedWe === we ? "acc" : ""}`}
                  style={{ cursor: "pointer", fontSize: 11 }}
                  onClick={() => handleWeChip(we)}
                  title={`Räume der ${we} hervorheben + nach oben sortieren`}
                >
                  {we} · {weCounts.get(we)} R
                </button>
              ))}
              {focusedWe && (
                <button
                  type="button"
                  className="pill mono"
                  style={{ cursor: "pointer", fontSize: 11, color: "var(--fg-dim)" }}
                  onClick={() => setFocusedWe(null)}
                  title="Fokus aufheben"
                >
                  ✕ Fokus
                </button>
              )}
            </div>
          )}
          <div style={{ display: "grid", gap: 4, maxHeight: "72vh", overflow: "auto" }}>
            {orderedRooms.map((r) => (
              <RoomRow
                key={r.roomId}
                room={r}
                weMode={weMode}
                selected={
                  weMode ? weSelection.has(r.roomId) : r.roomId === selectedRoomId
                }
                onClick={() =>
                  weMode
                    ? toggleWeSelection(r.roomId)
                    : setSelectedRoomId((id) => (id === r.roomId ? null : r.roomId))
                }
                onOpenDetail={() => {
                  if (weMode) return;
                  setSelectedRoomId(r.roomId);
                  setDetailRoomId(r.roomId);
                }}
              />
            ))}
            {plan.aussenwand && plan.aussenwand.schichten.length > 0 && (
              <div
                style={{
                  padding: "8px 10px",
                  border: "1px solid var(--acc)",
                  borderRadius: 6,
                  background: "var(--acc-tint, oklch(0.72 0.15 250 / 0.08))",
                  display: "grid",
                  gap: 3,
                  marginTop: 4,
                }}
                title="Außenwand-Schichten-Mengen — aus den mehrschaligen Außenwand-Layern abgeleitet (Innenwände zeichnest du selbst)."
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <span style={{ fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <Icon name="layers" size={14} />
                    Außenwand (Schichten)
                  </span>
                  <span className="mono dim" style={{ fontSize: 11 }}>
                    {plan.aussenwand.laufLaengeM.toFixed(1)} m · h {plan.aussenwand.wandHoeheM.toFixed(2)} m
                  </span>
                </div>
                {plan.aussenwand.schichten.map((s) => (
                  <div
                    key={s.key}
                    className="mono"
                    style={{ fontSize: 11, color: "var(--fg-mute)", display: "flex", gap: 10, justifyContent: "space-between", flexWrap: "wrap" }}
                  >
                    <span>{s.key} · {(s.thicknessM * 100).toFixed(0)} cm</span>
                    <span>
                      {s.flaecheM2.toFixed(1)} m² · {s.volumenM3.toFixed(2)} m³
                      {s.stueck ? ` · ≈${s.stueck.toLocaleString("de-DE")} ${s.stkEinheit ?? "Stk"}` : ""}
                    </span>
                  </div>
                ))}
                <div style={{ fontSize: 10, color: "var(--fg-faint)" }}>
                  Lauflänge ={" "}
                  {plan.aussenwand.laufLaengeQuelle === "footprint"
                    ? "Gebäude-Außenkontur (Raum-Union, erfasst L-Formen)"
                    : "Bounding-Box-Umfang (Näherung)"}{" "}
                  · Erst-Schätzung
                </div>
              </div>
            )}
          </div>
        </section>
      </div>

      {prompt?.kind === "distance" && (
        <PromptModal
          title="Maßstab kalibrieren"
          label="Reale Distanz zwischen den zwei Punkten"
          unit="m"
          confirmLabel="Maßstab setzen"
          hint="Komma erlaubt · z.B. 5 für 5,00 m"
          onConfirm={confirmPrompt}
          onClose={() => setPrompt(null)}
        />
      )}
      {prompt?.kind === "raumhoehe" && (
        <PromptModal
          title="Raumhöhe ändern"
          label="Raumhöhe (für Wandflächen-Ableitung)"
          initialValue={String(mengen.raumhoeheM)}
          unit="m"
          confirmLabel="Übernehmen + Recompute"
          onConfirm={confirmPrompt}
          onClose={() => setPrompt(null)}
        />
      )}

      {detailRoom && (
        <RaumDetailModal
          tenantId={tenantId}
          anfrageId={anfrageId}
          planIndex={planIndex}
          ratio={plan.scale.ratio}
          room={detailRoom}
          onClose={() => setDetailRoomId(null)}
          onSaved={(m) => {
            setMengen(m);
            setDirty(true);
          }}
        />
      )}
    </div>
  );
}

function RoomRow({
  room,
  weMode,
  selected,
  onClick,
  onOpenDetail,
}: {
  room: MengenRoom;
  weMode: boolean;
  selected: boolean;
  onClick: () => void;
  onOpenDetail: () => void;
}) {
  const suspicious = isSuspiciousShape(room.bodenflaecheM2, room.umfangM);
  return (
    <button
      type="button"
      onClick={onClick}
      onDoubleClick={onOpenDetail}
      title={weMode ? "Klick: markieren / Markierung aufheben" : "Doppelklick: bemustern / Details"}
      className="block"
      style={{
        textAlign: "left",
        padding: "8px 10px",
        border: selected
          ? `1px solid ${weMode ? "var(--ok)" : "var(--acc)"}`
          : "1px solid var(--line, #2a2a2a)",
        background: selected
          ? weMode
            ? "oklch(0.78 0.16 152 / 0.1)"
            : "oklch(0.72 0.15 250 / 0.1)"
          : "transparent",
        cursor: "pointer",
        display: "grid",
        gap: 3,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
        <span style={{ fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6 }}>
          {weMode && (
            <span
              aria-hidden
              style={{
                display: "inline-block",
                width: 12,
                height: 12,
                borderRadius: 3,
                border: "1px solid var(--line)",
                background: selected ? "var(--ok)" : "transparent",
              }}
            />
          )}
          {room.label}
        </span>
        <span className="mono dim" style={{ fontSize: 11 }}>
          {room.din277.sub}
        </span>
      </div>
      <div className="mono" style={{ fontSize: 11, color: "var(--fg-mute)", display: "flex", gap: 10, flexWrap: "wrap" }}>
        <span>Boden {room.bodenflaecheM2.toFixed(2)} m²</span>
        <span>Wand {room.wandflaecheM2.toFixed(1)} m²</span>
        <span>U {room.umfangM.toFixed(1)} m</span>
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {room.wohneinheit && (
          <span
            className="pill acc mono"
            style={{ fontSize: 10 }}
            title="Zugeordnete Wohneinheit"
          >
            {room.wohneinheit}
          </span>
        )}
        {room.quelleBoden === "ngf-stempel" && room.ngfStempelM2 !== undefined && (
          <span
            className="pill acc mono"
            style={{ fontSize: 10 }}
            title="Bodenfläche aus dem NGF-Stempel des Plans übernommen (Vorrang vor der gezeichneten Polygonfläche)."
          >
            NGF {room.ngfStempelM2.toFixed(2)} m²
            {room.ngfDrift !== undefined &&
              Math.abs(room.ngfDrift - 1) > 0.03 &&
              ` · Δ ${room.ngfDrift > 1 ? "+" : ""}${Math.round((room.ngfDrift - 1) * 100)}%`}
          </span>
        )}
        {room.quelleBoden === "manuell" && (
          <span className="pill mono" style={{ fontSize: 10 }} title="Bodenfläche manuell gesetzt.">
            manuell
          </span>
        )}
        {!room.isInsideBuilding && (
          <span className="pill mono" style={{ fontSize: 10 }}>
            außen?
          </span>
        )}
        {suspicious && (
          <span className="pill bad mono" style={{ fontSize: 10 }} title="Umfang/√Fläche auffällig hoch — wahrscheinlich Topologie-Artefakt, bitte Polygon prüfen.">
            ⚠ Form prüfen
          </span>
        )}
      </div>
    </button>
  );
}

function NotGeneratedNotice() {
  return (
    <div className="ueber">
      <section className="block">
        <div className="block-head">
          <span className="uppercase-mini" style={{ color: "var(--acc)" }}>
            // MENGEN · PFAD B
          </span>
        </div>
        <div className="notice-body">
          <div>
            Noch keine Mengen vorhanden. Die Vektor-Mengen-Engine läuft als
            Phase 2B, wenn die Pipeline für diese Anfrage mit aktivierter
            Mengen-Engine durchläuft (
            <span className="mono dim">02-mengen.json</span> fehlt noch).
          </div>
          <div className="dim" style={{ fontSize: 12 }}>
            Starte die Pipeline („Pipeline starten") — bei einem Vektor-Plan
            entstehen Räume, Maßstab und Mengen, die hier editierbar werden.
          </div>
        </div>
      </section>
    </div>
  );
}
